import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, CheckCircle2, Shield, Users, Zap,
  FileText, DollarSign, MessageSquare, UserCheck, CalendarCheck, ClipboardList,
} from "lucide-react";
import { HomeLensLogo } from "@/components/HomeLensLogo";

const features = [
  {
    icon: <ClipboardList className="w-6 h-6" strokeWidth={1.5} />,
    title: "Transaction Timeline",
    description: "See where you are and what's next. Visual progress keeps everyone aligned."
  },
  {
    icon: <FileText className="w-6 h-6" strokeWidth={1.5} />,
    title: "Document Vault",
    description: "Store, share, and access important files in one secure place."
  },
  {
    icon: <DollarSign className="w-6 h-6" strokeWidth={1.5} />,
    title: "Offer Repository",
    description: "Review and compare offers side-by-side with confidence."
  },
  {
    icon: <MessageSquare className="w-6 h-6" strokeWidth={1.5} />,
    title: "Team Chat",
    description: "Message your agent, buyer, and seller without leaving the transaction."
  },
  {
    icon: <UserCheck className="w-6 h-6" strokeWidth={1.5} />,
    title: "Role-Based Access",
    description: "The right people see the right information at the right time."
  },
  {
    icon: <CalendarCheck className="w-6 h-6" strokeWidth={1.5} />,
    title: "Clear Next Steps",
    description: "Know what's coming up and stay ahead of every milestone."
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1A2B4F]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <HomeLensLogo className="text-white [&_svg]:text-white [&_span]:text-white" />
            <div className="flex items-center gap-3">
              {user ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="rounded-md px-6 h-10 text-sm font-medium bg-white text-[#1A2B4F] hover:bg-slate-100"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/login")}
                    className="rounded-md h-10 text-sm text-white/70 hover:text-white hover:bg-white/10"
                    data-testid="login-nav-btn"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => navigate("/register")}
                    className="rounded-md px-6 h-10 text-sm font-medium bg-white text-[#1A2B4F] hover:bg-slate-100"
                    data-testid="get-started-btn"
                  >
                    Start Your Journey
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pb-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — Copy */}
            <div>
              <div className="inline-flex items-center px-3.5 py-1.5 rounded-full border border-[#1A2B4F]/15 bg-[#1A2B4F]/[0.03] mb-8">
                <span className="text-[11px] font-bold text-[#1A2B4F] tracking-[0.15em] uppercase">Real Estate Simplified</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-slate-900 tracking-tight leading-[1.12] mb-5">
                From Offer to Close,<br />
                <span className="text-[#2E4A7F]">All in One Place.</span>
              </h1>
              <p className="text-base text-slate-500 leading-relaxed mb-9 max-w-[400px]">
                A clear lens into your transaction — track progress, share documents, and stay aligned every step of the way.
              </p>
              <div className="flex items-center gap-3 mb-10">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="rounded-md px-7 h-12 text-sm font-medium bg-[#1A2B4F] hover:bg-[#2E4A7F] transition-colors"
                  data-testid="hero-get-started-btn"
                >
                  Create Your Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="rounded-md px-7 h-12 text-sm font-medium border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="hero-sign-in-btn"
                >
                  Sign In
                </Button>
              </div>
              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2 text-[13px]">
                  <Shield className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                  Secure & Private
                </span>
                <span className="flex items-center gap-2 text-[13px]">
                  <Users className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                  Built for Agents & Clients
                </span>
                <span className="flex items-center gap-2 text-[13px]">
                  <Zap className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                  Get Started in Minutes
                </span>
              </div>
            </div>

            {/* Right — Hero Image with Timeline Card Overlay */}
            <div className="relative" data-testid="hero-preview">
              {/* Home background image */}
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/01fdaf44-65b8-46d1-b16d-0f9e2407745b/images/1cb938cdba1fe1515188a8d2160c2800c5f6d2b75d1eb29261c0fc2a5c91d108.png"
                  alt="Beautiful suburban home with Sold sign"
                  className="w-full h-[520px] object-cover"
                />
              </div>

              {/* Floating Timeline Card */}
              <div className="absolute bottom-8 left-5 right-5 sm:left-8 sm:right-8 bg-white rounded-xl shadow-elevated p-6 border border-slate-100">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-[15px] font-bold text-slate-900">Transaction Timeline</h3>
                    <p className="text-xs text-slate-400 mt-0.5">123 Main Street</p>
                  </div>
                  <span className="text-[10px] font-bold text-[#1A2B4F] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full uppercase tracking-wider">In Contract</span>
                </div>

                {/* 6-Stage Timeline */}
                <div className="flex items-start justify-between mb-5">
                  {[
                    { label: "Offer\nAccepted", done: true },
                    { label: "Inspection\nScheduled", done: true },
                    { label: "Appraisal\n& Loan", active: true, num: 3 },
                    { label: "Title", num: 4 },
                    { label: "Final\nWalkthrough", num: 5 },
                    { label: "Closing", num: 6 },
                  ].map((stage, index, arr) => (
                    <div key={index} className="flex items-start flex-1">
                      <div className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          stage.done ? 'bg-emerald-500 text-white' :
                          stage.active ? 'bg-[#1A2B4F] text-white ring-4 ring-blue-100' :
                          'bg-slate-100 text-slate-400'
                        }`}>
                          {stage.done ? <CheckCircle2 className="w-[18px] h-[18px]" /> : (stage.num || index + 1)}
                        </div>
                        <span className={`text-[9px] mt-2 text-center leading-tight whitespace-pre-line ${
                          stage.active ? 'font-bold text-[#1A2B4F]' : stage.done ? 'font-semibold text-slate-600' : 'text-slate-400'
                        }`}>
                          {stage.label.toUpperCase()}
                        </span>
                      </div>
                      {index < arr.length - 1 && (
                        <div className={`flex-1 h-[2.5px] mx-1.5 mt-[18px] rounded-full ${
                          stage.done ? 'bg-emerald-500' : stage.active ? 'bg-emerald-400' : 'bg-slate-100'
                        }`}></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Next Step */}
                <div className="bg-slate-50 border border-slate-100 rounded-lg p-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#1A2B4F]/[0.07] flex items-center justify-center shrink-0">
                      <CalendarCheck className="w-4 h-4 text-[#1A2B4F]" strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-800">Next Up: Appraisal & Loan Approval</p>
                      <p className="text-[11px] text-slate-400">Stay on track and never miss a deadline.</p>
                    </div>
                  </div>
                  <span className="text-[12px] font-semibold text-blue-600 shrink-0 ml-4">View Details</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-slate-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-[#2E4A7F] tracking-[0.2em] uppercase mb-3">Everything You Need</p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-3">
              One Place for Every Part of the Process
            </h2>
            <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed">
              Stop juggling emails and scattered files. HomeLens brings clarity, organization, and confidence to every transaction.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-100 p-7 shadow-soft hover:shadow-float transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#1A2B4F]/[0.04] border border-[#1A2B4F]/[0.06] flex items-center justify-center text-[#2E4A7F] mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-1.5">
                  {feature.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A2B4F] py-20 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-heading text-2xl lg:text-[1.75rem] font-bold text-white tracking-tight mb-2">
              Ready to Simplify Your Transactions?
            </h2>
            <p className="text-[#AAB7D1] text-sm">
              Join agents and home buyers who trust HomeLens.
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => navigate("/register")}
            className="rounded-md px-8 h-12 text-sm font-medium bg-white text-[#1A2B4F] hover:bg-slate-100 transition-colors shrink-0"
            data-testid="cta-get-started-btn"
          >
            Get Started Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <HomeLensLogo size="sm" />
            <p className="text-xs text-slate-400">
              &copy; 2026 HomeLens. Simplifying real estate transactions.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer transition-colors">Privacy Policy</span>
            <span className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer transition-colors">Terms of Service</span>
            <span className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer transition-colors">Contact Support</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
